import toPairs from 'lodash/toPairs';
import curry from 'lodash/curry';
import cloneDeep from 'lodash/cloneDeep';
import upperFirst from 'lodash/upperFirst';
import set from 'lodash/set';
import map from 'lodash/map';

export function ellipsify(str, max) {
  const safeStr = str ? str : '';
  return safeStr.length > max ? `${str.substr(0, max)}...` : str;
}

export const modifyStyle = curry((keyToChange, newValue, component, props) => {
  const style = cloneDeep(props.style);
  const newProps = { ...props, style: set(style, keyToChange, newValue) };
  return component(newProps);
});

export function boldify(targettedString, extract) {
  if (!extract) {
    return targettedString;
  }

  const index = targettedString.toLowerCase().indexOf(extract.toLowerCase());

  return index > -1 ? (
    <span>
      {targettedString.slice(0, index)}
      <strong>{targettedString.slice(index, index + extract.length)}</strong>
      {targettedString.slice(index + extract.length, targettedString.length)}
    </span>
  ) : (
    targettedString
  );
}

export const componentsFromList = curry(
  (listOfElementKey, component, props) => {
    let listOfElement;
    if (typeof listOfElementKey === 'string') {
      listOfElement = props[listOfElementKey];
    }
    if (Array.isArray(listOfElementKey)) {
      listOfElement = listOfElementKey;
    }
    return (
      listOfElement &&
      listOfElement.map((item, index) => {
        const newProps = { ...props, index, item, key: index };
        return component(newProps);
      })
    );
  },
);

export const getContrastYIQ = (color, colors) => {
  const hex = '#';
  let r, g, b;
  if (color.indexOf(hex) > -1) {
    r = parseInt(color.substr(1, 2), 16);
    g = parseInt(color.substr(3, 2), 16);
    b = parseInt(color.substr(5, 2), 16);
  } else {
    color = color.match(/\d+/g);
    r = color[0];
    g = color[1];
    b = color[2];
  }

  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? colors.textColor : colors.textColorNegative;
};

// locales list considered as US
const USLocales = ['en_US', 'en_CA', 'es_US'];

export function isUSuser(userLocale) {
  return USLocales.some(item => userLocale === item);
}

export const removeParamFromQuery = (query, param) =>
  toPairs(query)
    .filter(item => item[0] !== param)
    .reduce(
      (prev, item) => `${prev === '' ? '' : `${prev}&`}${item[0]}=${item[1]}`,
      '',
    );

export const waURL = input => {
  const pos = input.indexOf('://');

  if (pos > -1) {
    return input.substr(pos + 2);
  }

  return input;
};

// This helper should be passed as onKeyDown handler function together
// with a callback function which will get called if the key is either
// Enter or Space
export const onEnterOrSpace =
  <T extends HTMLElement>(
    cb: ((e: React.KeyboardEvent<T>) => void) | null | undefined,
  ) =>
  (e: React.KeyboardEvent<T>) => {
    if (['Enter', ' '].includes(e.key)) {
      e.stopPropagation();
      e.preventDefault(); // stop space from scrolling the page

      if (cb) {
        cb(e);
      }
    }
  };

// This helper should be passed as ref if we want to focus that element
// when the element is rendered. It accepts an optional callback which
// will get called with the element.
export const refFocus =
  (cb?: (el: HTMLElement) => void) => (el: HTMLElement | null) => {
    if (el) {
      setTimeout(() => {
        (el || {}).focus();
        if (cb) cb(el);
      }, 300);
    }
  };

export const startCase = (str: string) =>
  map(str.split(' '), upperFirst).join(' ');

export const focusFirstElement = (parentRef: HTMLElement | null) => {
  const firstFocusableElement = parentRef?.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
  )[0] as HTMLElement | null;

  if (firstFocusableElement) {
    firstFocusableElement.focus();
  }
};
